import { PushNotifications } from '@capacitor/push-notifications';
import { Capacitor } from '@capacitor/core';
import { Badge } from '@capawesome/capacitor-badge';
import { supabase } from '../supabaseClient';
import { ensurePushDeviceId, getHomeownerToken, getStoredPushToken, setStoredPushToken } from './storage';
import { deactivateDeviceTokens, deviceTokenRegistration, upsertDeviceToken } from '../shared/deviceTokens';
import { homeownerRecords } from './homeownerApi';
import { publishPushOpen } from './pushNavigation';
import { publishAppRefresh } from './appRefresh';
import { devLog } from './devLog';

let listening = false;
let activeUserId = null;
let latestToken = null;

/** The APNs/FCM token for this install. Logout uses it so the other phone stays registered. */
export function setActiveDeviceToken(token) {
  latestToken = typeof token === 'string' && token ? token : null;
  if (latestToken) {
    Promise.resolve()
      .then(() => setStoredPushToken(latestToken))
      .catch(() => {});
  }
  return latestToken;
}

/** Memory first, then the token saved the last time this install registered. */
async function devicePushToken() {
  if (latestToken) return latestToken;
  const stored = await getStoredPushToken();
  if (typeof stored === 'string' && stored) {
    latestToken = stored;
    return stored;
  }
  return '';
}

/**
 * Point later token saves at the signed-in user. Logout passes null so a
 * token event cannot be written onto the previous account.
 */
export async function bindPushUser(userId) {
  activeUserId = userId || null;
  if (!Capacitor.isNativePlatform() || !activeUserId || !latestToken) return;
  await saveDeviceToken(activeUserId, latestToken);
}

/** 'granted', 'denied', 'prompt', or 'web' when this is not the native app. */
export async function readPushPermission() {
  if (!Capacitor.isNativePlatform()) return 'web';
  try {
    const status = await PushNotifications.checkPermissions();
    const receive = status && status.receive;
    if (receive === 'granted' || receive === 'denied') return receive;
    return 'prompt';
  } catch (error) {
    return 'prompt';
  }
}

async function badgeAlreadyGranted() {
  if (!Capacitor.isNativePlatform()) return false;
  try {
    const status = await Badge.checkPermissions();
    return Boolean(status && status.display === 'granted');
  } catch (error) {
    return false;
  }
}

/** Clears the icon badge. Never asks for permission. */
export async function clearAppBadge() {
  if (!(await badgeAlreadyGranted())) return;
  try {
    await Badge.clear();
  } catch (error) {
    devLog('Badge clear skipped');
  }
}

/**
 * Android 8+ drops a notification that has no channel. "default" matches
 * the FCM payload. Importance 4 is high. This does not touch Firebase.
 */
async function ensureAndroidNotificationChannel() {
  if (Capacitor.getPlatform() !== 'android') return;
  try {
    await PushNotifications.createChannel({
      id: 'default',
      name: 'Default',
      description: 'General notifications',
      importance: 4,
      visibility: 1,
      vibration: true,
    });
  } catch (error) {
    devLog('Android notification channel skipped');
  }
}

/**
 * register() rejects when Firebase is not initialized. That rejection must
 * not take the app down. A later build with google-services.json still
 * registers, because the native call runs once Firebase is present.
 */
async function registerDeviceForPush() {
  await ensureAndroidNotificationChannel();
  try {
    await PushNotifications.register();
    devLog('Push notifications registered');
  } catch (error) {
    console.error('Push registration skipped');
  }
}

/** Sets the icon badge. Never asks for permission. */
export async function setAppBadge(count) {
  if (!(await badgeAlreadyGranted())) return;
  const visible = Number(count) || 0;
  try {
    if (visible > 0) await Badge.set({ count: visible });
    else await Badge.clear();
  } catch (error) {
    devLog('Badge update skipped');
  }
}

/**
 * Initialize push notifications for the current user.
 * Listeners are registered once. Login and logout re-save the token for
 * whoever is signed in now.
 */
export const initializePushNotifications = async (userId) => {
  if (!Capacitor.isNativePlatform()) {
    devLog('Push notifications only available on native platforms');
    return;
  }
  if (!userId) return;

  activeUserId = userId;

  try {
    if (!listening) {
      await PushNotifications.removeAllListeners();

      await PushNotifications.addListener('registration', async (token) => {
        setActiveDeviceToken(token && token.value ? token.value : '');
        const user = activeUserId;
        if (!user || !latestToken) return;
        devLog('Push registration success');
        await saveDeviceToken(user, latestToken);
      });

      await PushNotifications.addListener('registrationError', (error) => {
        console.error('Error on registration:', error && error.error ? error.error : 'registration failed');
      });

      await PushNotifications.addListener('pushNotificationReceived', () => {
        devLog('Push notification received in the foreground');
        publishAppRefresh('push');
      });

      await PushNotifications.addListener('pushNotificationActionPerformed', (notification) => {
        const note = notification && (notification.notification || notification);
        const data = (note && note.data) || {};
        devLog('Push notification opened');
        publishPushOpen(data);
        publishAppRefresh('push-open');
      });

      listening = true;

      const permStatus = await PushNotifications.requestPermissions();
      if (permStatus.receive === 'granted') {
        await registerDeviceForPush();
      } else {
        devLog('Push notification permission denied');
      }
      return;
    }

    if (latestToken && activeUserId) {
      await saveDeviceToken(activeUserId, latestToken);
    }
    const permStatus = await PushNotifications.checkPermissions();
    if (permStatus.receive === 'granted') {
      await registerDeviceForPush();
    }
  } catch (error) {
    console.error('Error setting up push notification listeners:', error);
  }
};

function apnsEnvironmentForInstall() {
  const configured = String(process.env.REACT_APP_APNS_ENVIRONMENT || '').trim().toLowerCase();
  if (configured === 'sandbox' || configured === 'production') return configured;
  if (typeof window !== 'undefined') {
    const host = String(window.location.hostname || '').toLowerCase();
    const protocol = String(window.location.protocol || '').toLowerCase();
    if (host === 'localhost' || host === '127.0.0.1' || host.endsWith('.netlify.app') || protocol === 'capacitor:') {
      return 'sandbox';
    }
  }
  return '';
}

const saveDeviceToken = async (userId, token) => {
  try {
    const platform = Capacitor.getPlatform();
    const deviceId = await ensurePushDeviceId();
    const record = { token, platform, is_active: true };
    const environment = apnsEnvironmentForInstall();
    if (environment) record.apnsEnvironment = environment;
    if (deviceId) record.deviceId = deviceId;
    const registration = deviceTokenRegistration(userId, record);
    if (registration.error) {
      console.error('Error saving device token:', registration.error);
      return;
    }

    if (await getHomeownerToken()) {
      await homeownerRecords({
        resource: 'device_tokens',
        action: 'upsert',
        record,
      });
      devLog('Device token saved for the homeowner');
      return;
    }

    const row = registration.row;
    row.updated_at = new Date().toISOString();
    const saved = await upsertDeviceToken(supabase, row);

    if (saved.error) {
      console.error('Error saving device token:', saved.error);
    } else {
      devLog('Device token saved to database');
    }
  } catch (error) {
    console.error('Error saving device token:', error);
  }
};

async function logoutRecord() {
  const token = await devicePushToken();
  const deviceId = await ensurePushDeviceId();
  const record = {};
  if (token) record.token = token;
  if (deviceId) record.deviceId = deviceId;
  return record;
}

export const removeDeviceToken = async (userId) => {
  try {
    const record = await logoutRecord();

    if (await getHomeownerToken()) {
      await homeownerRecords({
        resource: 'device_tokens',
        action: 'deactivate',
        record,
      });
      devLog('Homeowner device token marked as inactive');
      return;
    }

    if (!userId) return;
    await deactivateDeviceTokens(supabase, userId, record);
    devLog('Device token marked as inactive');
  } catch (error) {
    console.error('Error removing device token:', error);
  }
};

export const checkPushPermissions = async () => {
  if (!Capacitor.isNativePlatform()) {
    return { receive: 'prompt' };
  }

  const permStatus = await PushNotifications.checkPermissions();
  return permStatus;
};
